import { useEffect, useState } from "react";
import { api } from "./api";
import { getAccessToken } from "./auth";

export type AttachmentKind = "image" | "audio" | "file";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8788";

/**
 * Uploads a file through the Sebas API, which relays it to R2.
 *
 * The browser does not PUT to R2 directly. That would be a cross-origin request
 * needing a CORS rule on the bucket, which cannot be set with an object-scoped
 * R2 token — and could never cover the Tauri shells' `tauri://` origin. Going via
 * the API removes that dependency, so uploads work everywhere with no extra setup.
 */
export async function uploadFile(
  file: File | Blob,
  kind: AttachmentKind,
  opts: { filename?: string; noteId?: string; durationMs?: number } = {},
): Promise<string> {
  const filename = opts.filename ?? (file instanceof File ? file.name : `${kind}-${Date.now()}`);

  const params = new URLSearchParams({ kind, filename });
  if (opts.noteId) params.set("noteId", opts.noteId);
  if (opts.durationMs) params.set("durationMs", String(Math.round(opts.durationMs)));

  const token = await getAccessToken();
  const res = await fetch(`${API_URL}/v1/uploads?${params}`, {
    method: "POST",
    headers: {
      // The raw bytes are the body; the type drives server-side validation.
      "content-type": file.type || "application/octet-stream",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: file,
  });

  if (!res.ok) {
    const detail = await res
      .json()
      .then((b) => (b as { error?: string }).error)
      .catch(() => null);
    throw new Error(detail ?? `Upload failed (${res.status})`);
  }

  const { attachmentId } = (await res.json()) as { attachmentId: string };
  return attachmentId;
}

/**
 * Resolves an attachment id to a readable URL.
 *
 * Presigned URLs expire, so documents store the attachment *id* and the view
 * resolves it on demand. Results are cached just under their TTL, shared across
 * every component showing the same attachment.
 */
const cache = new Map<string, { url: string; expiresAt: number }>();
const inflight = new Map<string, Promise<string>>();

export async function attachmentUrl(attachmentId: string): Promise<string> {
  const hit = cache.get(attachmentId);
  if (hit && Date.now() < hit.expiresAt) return hit.url;

  const existing = inflight.get(attachmentId);
  if (existing) return existing;

  const p = api<{ url: string; expiresIn: number }>(`/v1/uploads/${attachmentId}/url`)
    .then((res) => {
      // Refresh 5 minutes early so a long-open tab never serves a dead URL.
      cache.set(attachmentId, { url: res.url, expiresAt: Date.now() + (res.expiresIn - 300) * 1000 });
      return res.url;
    })
    .finally(() => inflight.delete(attachmentId));

  inflight.set(attachmentId, p);
  return p;
}

export function useAttachmentUrl(attachmentId: string | null | undefined) {
  const [url, setUrl] = useState<string | null>(() =>
    attachmentId ? (cache.get(attachmentId)?.url ?? null) : null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!attachmentId) return;
    let cancelled = false;
    attachmentUrl(attachmentId)
      .then((u) => !cancelled && setUrl(u))
      .catch((e) => !cancelled && setError(String(e)));
    return () => {
      cancelled = true;
    };
  }, [attachmentId]);

  return { url, error };
}
